import { useEffect, useRef, useState } from 'react';
import { deckLabel } from '@edh/shared';
import {
  LETHAL_COMMANDER_DAMAGE,
  LETHAL_POISON,
  TAKEDOWN_LEVEL,
  bountiesFor,
  columnsOf,
  commanderKey,
  foldAmendments,
  isDown,
  isOut,
  lifeSeries,
  seatsInOrder,
  skillOf,
  stepBack,
  turnStepsOf,
  type DerivedSeat,
  type LiveGame,
  type LiveView,
} from '@edh/shared';
import { Avatar } from './Avatar';
import { CommentaryFeedSheet, CommentaryFlash } from './Commentary';
import { LifeTimeline } from './LifeTimeline';
import { Sheet } from './Sheet';
import { markOut, passTurn, reorderTable, takeTurnBack, type LiveSession } from '../lib/live';
import { AMEND_WINDOW_MS } from '@edh/shared';
import type { CommentaryFeed } from '../lib/commentary';
import { useLookups, useStore } from '../lib/store';

/**
 * Drawn rather than typed.
 *
 * A − or + glyph is centred by its own font metrics, which are built for a line
 * of text and not for the middle of a circle — no amount of flex centring moves
 * it, because the box is already centred and the ink inside it is not. Two SVG
 * strokes are centred by geometry.
 */
function Minus() {
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
      <line x1="9" y1="17" x2="25" y2="17" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
    </svg>
  );
}

function Plus() {
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
      <line x1="9" y1="17" x2="25" y2="17" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="17" y1="9" x2="17" y2="25" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The stroke so far, with the time left to change your mind about it.
 *
 * Nine down and two back up is one stroke of seven, and the app will fold them
 * together for as long as this ring has anything left in it — so the ring is
 * the app showing its working. Without it the window is invisible and the rule
 * has to be learned by being surprised twice.
 */
function Stroke({ delta, at }: { delta: number; at: number }) {
  const [left, setLeft] = useState(1);
  const frame = useRef(0);

  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(0, at + AMEND_WINDOW_MS - Date.now()) / AMEND_WINDOW_MS;
      setLeft(remaining);
      if (remaining > 0) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [at]);

  const R = 15;
  const C = 2 * Math.PI * R;

  return (
    <span className={`stroke ${delta > 0 ? 'up' : 'down'}`}>
      <svg className="stroke-ring" viewBox="0 0 34 34" aria-hidden="true">
        <circle cx="17" cy="17" r={R} className="stroke-track" />
        <circle
          cx="17"
          cy="17"
          r={R}
          className="stroke-left"
          strokeDasharray={C}
          strokeDashoffset={C * (1 - left)}
        />
      </svg>
      <span className="stroke-value num">
        {delta > 0 ? '+' : ''}
        {delta}
      </span>
    </span>
  );
}

export type CounterMode = 'table' | 'player';

/**
 * The counter, in one of two shapes.
 *
 * **Table** is the phone in the middle: seats sit where the people do and the
 * far side is rotated 180° so it reads the right way up from over there. A
 * counter you have to walk round the table to read is slower than the notepad
 * it replaces, and anything slower than the notepad will not get used.
 *
 * **Player** is the phone in your hand: your seat enormous at the bottom where
 * your thumb already is, everyone else small along the top. The rotated grid is
 * exactly wrong here — nobody else is reading your screen, so three quarters of
 * it was being spent facing people who cannot see it.
 *
 * Both are views of one document. Whatever anybody taps on either lands in the
 * same log, and the totals are derived, so they cannot drift apart.
 */
export function LifeCounter({
  session,
  mode,
  onMode,
  isHost,
  onEnd,
  feed,
}: {
  session: LiveSession;
  mode: CounterMode;
  onMode: (mode: CounterMode) => void;
  isHost: boolean;
  onEnd: () => void;
  /** Owned by Play, so the lobby and the table read the same one. */
  feed: CommentaryFeed;
}) {
  const { me } = useStore();
  const [damageSeat, setDamageSeat] = useState<number | null>(null);
  const [poisonSeat, setPoisonSeat] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);
  const [showPoison, setShowPoison] = usePoisonCounters();

  // Immersive from the moment the table appears: a game is the one screen that
  // wants everything, and the menu in the hub is always there to get out.
  useEffect(() => {
    document.body.classList.add('immersive');
    return () => {
      // Leaving it set on the way out would strand the rest of the app with no
      // header and no tab bar.
      document.body.classList.remove('immersive');
    };
  }, []);

  const { game, view } = session;
  if (!game || !view) return null;

  const mySeat = view.seats.findIndex((s) => s.pilotId === me?.id);
  // Somebody watching a game they are not in gets the table, not a personal
  // view of a seat they do not have.
  const layout: CounterMode = mode === 'player' && mySeat >= 0 ? 'player' : 'table';

  const shared = {
    session,
    view,
    showPoison,
    onCommander: setDamageSeat,
    onPoison: setPoisonSeat,
  };

  return (
    <>
      {layout === 'table' ? (
        <TableLayout {...shared} onMenu={() => setMenuOpen(true)} />
      ) : (
        <PlayerLayout
          {...shared}
          mySeat={mySeat}
          isHost={isHost}
          onMenu={() => setMenuOpen(true)}
        />
      )}

      {/*
        One line of the feed, over whichever layout is up.
        
        It was table-only at first, on the reasoning that a post is worth a
        glance between turns and not worth a corner of somebody's own pad. Then
        the default view became your own seat, which meant the person most
        likely to be holding the phone was the one person who never saw a post
        land. It floats above both now; it is gone in eight seconds either way.
      */}
      <CommentaryFlash feed={feed} onOpen={() => setFeedOpen(true)} />

      {feedOpen && <CommentaryFeedSheet feed={feed} onClose={() => setFeedOpen(false)} />}

      {menuOpen && (
        <GameMenu
          game={game}
          view={view}
          session={session}
          isHost={isHost}
          mode={layout}
          mySeat={mySeat}
          onFeed={() => {
            setMenuOpen(false);
            setFeedOpen(true);
          }}
          canSwitch={mySeat >= 0}
          onMode={onMode}
          showPoison={showPoison}
          onShowPoison={setShowPoison}
          onEnd={onEnd}
          onClose={() => setMenuOpen(false)}
        />
      )}

      {damageSeat !== null && (
        <CommanderDamage
          session={session}
          view={view}
          seatIndex={damageSeat}
          onClose={() => setDamageSeat(null)}
        />
      )}

      {poisonSeat !== null && (
        <Poison
          session={session}
          view={view}
          seatIndex={poisonSeat}
          onClose={() => setPoisonSeat(null)}
        />
      )}
    </>
  );
}

/**
 * Whether this phone shows the poison counter on every pad.
 *
 * Off by default, because most Commander games contain no poison at all and a
 * skull reading zero on four pads all evening is four pieces of furniture. The
 * pads are small and every millimetre of them is contested.
 *
 * A preference on the device rather than a setting on the guild: it is about
 * how somebody likes their counter, not about how the league is run, and
 * needing a leader to turn it on for the one night somebody brought infect
 * would be absurd.
 */
const POISON_KEY = 'edh.poison';

function usePoisonCounters(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return window.localStorage.getItem(POISON_KEY) === '1';
    } catch {
      // Private browsing, or storage disabled. The default is fine.
      return false;
    }
  });

  return [
    on,
    (next: boolean) => {
      setOn(next);
      try {
        window.localStorage.setItem(POISON_KEY, next ? '1' : '0');
      } catch {
        // It still works for this game; it just will not be remembered.
      }
    },
  ];
}

/**
 * Declaring somebody out, or putting them back in.
 *
 * The server works out where the turn goes afterwards, because it is the only
 * one that can be sure who was still in when it did.
 */
function useOut(session: LiveSession) {
  const [busy, setBusy] = useState(false);
  /** The seat waiting on "who knocked you out?", or null when nothing is. */
  const [asking, setAsking] = useState<number | null>(null);

  function write(seat: number, out: boolean, by: string | null) {
    const id = session.game?.id;
    if (!id || busy) return;
    setBusy(true);
    void markOut(id, seat, out, by)
      .then(session.put)
      .catch(() => void session.refresh())
      .finally(() => setBusy(false));
  }

  /*
   * Only a pod is asked. Heads-up there is exactly one candidate and the answer
   * is worth nothing — the takedown bonus does not pay in a duel, where the
   * only elimination available is the win itself — so the question would be a
   * tap that changes nothing.
   */
  const pod = (session.view?.seats.length ?? 0) >= 3;

  return {
    busy,
    asking,
    set: (seat: number, out: boolean) => {
      // Going out asks who did it. Coming back in never does: putting somebody
      // back is undoing a mistake, and it takes the credit with it.
      if (out && pod) setAsking(seat);
      else write(seat, out, null);
    },
    /** Null is a real answer — a concede, or a wipe that caught its own caster. */
    confirm: (by: string | null) => {
      if (asking === null) return;
      write(asking, true, by);
      setAsking(null);
    },
    cancel: () => setAsking(null),
  };
}

type OutControl = ReturnType<typeof useOut>;

/**
 * Who knocked this seat out.
 *
 * Asked rather than inferred, and that is the whole point of the screen. The
 * counter knows a life total reached zero; it has no idea whose spell took it
 * there, and the difference matters now that the answer is worth rating points.
 * Whoever held priority would be wrong often enough — board wipes, your own
 * commander, a trigger nobody remembers pointing — to be worth nothing.
 *
 * "Nobody" sits with the rest rather than off to one side, because for a
 * concede it is the correct answer and not a way of skipping the question.
 */
function TakedownSheet({ view, out }: { view: LiveView; out: OutControl }) {
  const { pilot } = useLookups();
  const seat = out.asking;
  if (seat === null) return null;
  const victim = pilot(view.seats[seat]?.pilotId ?? '');

  /*
   * Everyone else, whether or not they are still in it. Somebody who went out
   * two turns ago can absolutely have left the thing that finishes you, and a
   * list that hides them is a list that makes the table pick a wrong answer.
   */
  const candidates = view.seats.filter((s) => s.index !== seat && s.pilotId);

  return (
    <Sheet title={`Who knocked ${victim?.name ?? 'them'} out?`} onClose={out.cancel}>
      <div className="list">
        {candidates.map((s) => {
          const p = pilot(s.pilotId ?? '');
          return (
            <button
              key={s.index}
              className="list-item"
              style={{ border: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
              disabled={out.busy}
              onClick={() => out.confirm(s.pilotId)}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <Avatar name={p?.name ?? '?'} src={p?.avatar} size={30} />
                <span>{p?.name}</span>
              </span>
              {isOut(s) && <span className="sub">already out</span>}
            </button>
          );
        })}
        <button
          className="list-item"
          style={{ border: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
          disabled={out.busy}
          onClick={() => out.confirm(null)}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Sized and shaped like the avatars above it, so the row lines up
                with the rest of the list rather than sitting proud of it. */}
            <span
              aria-hidden="true"
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                border: '1px dashed var(--hairline)',
                color: 'var(--muted)',
                flexShrink: 0,
              }}
            >
              —
            </span>
            <span>Nobody</span>
          </span>
          <span className="sub">conceded, or their own doing</span>
        </button>
      </div>
    </Sheet>
  );
}

/** Passing the turn is where the server decides who is next; this just asks. */
function useTurn(session: LiveSession) {
  const [busy, setBusy] = useState(false);
  // Both directions go through the same guard, so the two buttons can never be
  // in flight at once and land in the order the network felt like.
  const send = (go: (id: string) => Promise<LiveGame>) => () => {
    const id = session.game?.id;
    if (!id || busy) return;
    setBusy(true);
    void go(id)
      .then(session.put)
      // A refused pass (not your turn) or a dropped one both resolve on the
      // next poll, which is two seconds away.
      .catch(() => void session.refresh())
      .finally(() => setBusy(false));
  };
  return { busy, pass: send(passTurn), back: send(takeTurnBack) };
}

/**
 * One seat, as a pad.
 *
 * Shared by both layouts, because they are the same object seen from two
 * places. The differences are all arguments: which way up it is drawn, whether
 * this phone is allowed to end that seat's turn, and whether it is yours.
 *
 * Everything inside sizes against the pad rather than the screen — a pad is the
 * whole width in a head-to-head and a third of it beside two opponents, and one
 * fixed button size cannot serve both.
 */
function SeatPad({
  seat,
  session,
  view,
  flipped,
  mine,
  canPassTurn,
  onCommander,
  onPoison,
  showPoison,
  onOut,
  outBusy,
  turnBusy,
  onPassTurn,
  canTakeBack,
  onTakeBack,
}: {
  seat: DerivedSeat;
  session: LiveSession;
  view: LiveView;
  flipped: boolean;
  mine: boolean;
  canPassTurn: boolean;
  onCommander: (seat: number) => void;
  onPoison: (seat: number) => void;
  /** A preference on this phone. The counter still appears if there is any. */
  showPoison: boolean;
  onOut: (seat: number, out: boolean) => void;
  outBusy: boolean;
  turnBusy: boolean;
  onPassTurn: () => void;
  /** This is the seat the turn would go back to, and you are allowed to do it. */
  canTakeBack: boolean;
  onTakeBack: () => void;
}) {
  const { pilot, deck } = useLookups();
  const i = seat.index;
  const p = pilot(seat.pilotId ?? '');
  const d = deck(seat.deckId ?? '');
  const isActive = i === view.activeSeat;
  const shown = session.lifeOf(i);
  const stroke = session.strokeOf(i);
  const gone = isOut(seat);
  /*
   * The numbers say they should be dead and nobody has said so yet.
   *
   * Not the same thing as being out, and the pad does not pretend it is: it
   * asks. Zero life ends nothing on its own in Commander — there is a response
   * to be had, a trigger to point at, or a thumb landed on the wrong pad.
   */
  const asking = !gone && isDown(seat);

  return (
    <div
      className={`seat-pad${flipped ? ' flipped' : ''}${isActive ? ' active' : ''}${
        gone ? ' out' : ''
      }${asking ? ' asking' : ''}${mine ? ' mine' : ''}`}
    >
      <span className="seat-pad-accent" />

      {/* Only the counters up here now. The name has gone down to sit above
          the commander, where it gets a line to itself instead of whatever the
          counters left of one. */}
      <div className="seat-pad-head">
        <span className="seat-pad-counters">
          <button
            className="seat-pad-cmd"
            onClick={() => onCommander(i)}
            aria-label={`commander damage on ${p?.name}`}
          >
            ⚔ {Object.values(seat.commanderDamage).reduce((a, b) => Math.max(a, b), 0) || 0}
          </button>
          {/* Hidden by default, but never once there is any on the table —
              a counter that vanishes while it is holding a number somebody put
              there is a counter nobody trusts again. */}
          {(showPoison || seat.poison > 0) && (
            <button
              className={`seat-pad-cmd${seat.poison >= LETHAL_POISON ? ' lethal' : ''}`}
              onClick={() => onPoison(i)}
              aria-label={`poison counters on ${p?.name}`}
            >
              ☠ {seat.poison}
            </button>
          )}
        </span>
      </div>

      <div className="seat-pad-life">
        <button
          className="life-step"
          onClick={() => session.bumpLife(i, -1)}
          aria-label={`minus one life for ${p?.name}`}
        >
          <Minus />
        </button>
        <span className="life-total-wrap">
          <span className="life-total num">{shown}</span>
        </span>
        {/* A child of the row, not of the number — it is positioned against the
            row's top edge, which does not move when the digits change. */}
        {stroke && stroke.delta !== 0 && <Stroke delta={stroke.delta} at={stroke.at} />}
        <button
          className="life-step"
          onClick={() => session.bumpLife(i, 1)}
          aria-label={`plus one life for ${p?.name}`}
        >
          <Plus />
        </button>
      </div>

      {/* Whose pad this is, then what they brought — or the button to end their
          turn, which belongs to whoever is taking it and inside their own pad
          arrives already the right way up. */}
      <div className="seat-pad-foot">
        <span className="seat-pad-who">{p?.name}</span>
        {gone ? (
          <button className="seat-out-mark" disabled={outBusy} onClick={() => onOut(i, false)}>
            Out · back in?
          </button>
        ) : asking ? (
          /* Above End Turn on purpose. If somebody is sitting on zero it is the
             question the table is actually in the middle of, and answering it
             passes the turn along anyway. */
          <button className="seat-out-ask" disabled={outBusy} onClick={() => onOut(i, true)}>
            Out?
          </button>
        ) : isActive && canPassTurn ? (
          <button className="seat-end-turn" disabled={turnBusy} onClick={onPassTurn}>
            End Turn
          </button>
        ) : canTakeBack ? (
          /* On the pad of whoever ended their turn, which is the seat the table
             is asking about when somebody says "wait, go back". Below the two
             questions above it: a turn ended early is worth fixing, and
             somebody sitting on zero is worth fixing first. */
          <button className="seat-take-back" disabled={turnBusy} onClick={onTakeBack}>
            ↩ Still your turn
          </button>
        ) : (
          <span className="seat-pad-deck">{d ? deckLabel(d) : ''}</span>
        )}
      </div>
    </div>
  );
}

// ---- Table -----------------------------------------------------------------

function TableLayout({
  session,
  view,
  onCommander,
  onPoison,
  showPoison,
  onMenu,
}: {
  session: LiveSession;
  view: LiveView;
  onCommander: (seat: number) => void;
  onPoison: (seat: number) => void;
  showPoison: boolean;
  onMenu: () => void;
}) {
  const { pilot } = useLookups();
  const turn = useTurn(session);
  const out = useOut(session);
  const n = view.seats.length;
  const active = pilot(view.seats[view.activeSeat]?.pilotId ?? '');
  /* Which seat, if any, is owed its turn back. Null on the opening turn and
     once every pass has been given back — the game cannot reverse past its
     own first turn. */
  const back = session.game ? stepBack(session.game) : null;

  return (
    <div className={`table-view seats-${n}`}>
      {/* Drawn in the arrangement, acted on by seat index. `position` is where a
          pad sits on screen and decides which way up it is; `seat.index` is who
          it belongs to and never moves, so rearranging the table cannot send a
          tap to the wrong player. */}
      {seatsInOrder(view).map((seat, position) => (
        <SeatPad
          key={seat.pilotId ?? seat.index}
          seat={seat}
          session={session}
          view={view}
          /* Whoever is nearest the bottom of the screen is holding the phone, so
             their pad is the upright one and the far side is turned to face
             them. Three players already put seat one across the bottom alone. */
          flipped={n === 2 ? position === 0 : n === 3 ? position > 0 : position < 2}
          mine={false}
          canPassTurn
          onCommander={onCommander}
          onPoison={onPoison}
          showPoison={showPoison}
          onOut={out.set}
          outBusy={out.busy}
          turnBusy={turn.busy}
          onPassTurn={turn.pass}
          /* This phone is the table. If it can end anybody's turn it can hand
             one back, and the server agrees for the host. */
          canTakeBack={back?.seat === seat.index}
          onTakeBack={turn.back}
        />
      ))}

      {/* The hub: whose turn it is, which turn, and the way into everything
          else. One target in the middle of the table beats three, and it can be
          reached from any side. */}
      <button className="table-centre" onClick={onMenu} aria-label="Game menu">
        <Avatar name={active?.name ?? '?'} src={active?.avatar} size={36} />
        <span className="table-turn-label">Round</span>
        <span className="table-turn num">{view.turn}</span>
      </button>

      <TakedownSheet view={view} out={out} />
    </div>
  );
}

// ---- Player ----------------------------------------------------------------

/**
 * Your game, on your phone.
 *
 * The one number you touch is the size of your palm and sits where your hand
 * already is. Everyone else is a row along the top — you read those far more
 * often than you change them, and on the rare occasion somebody's phone is dead
 * you can still reach in and fix their total.
 */
function PlayerLayout({
  session,
  view,
  mySeat,
  isHost,
  onCommander,
  onPoison,
  showPoison,
  onMenu,
}: {
  session: LiveSession;
  view: LiveView;
  mySeat: number;
  isHost: boolean;
  onCommander: (seat: number) => void;
  onPoison: (seat: number) => void;
  showPoison: boolean;
  onMenu: () => void;
}) {
  const { pilot, deck } = useLookups();
  const turn = useTurn(session);
  const out = useOut(session);
  const mine = view.seats[mySeat]!;

  /*
   * What this seat earns for taking out each of the others, from the same
   * helper the engine pays from — so the number on screen is the number that
   * lands, not an estimate of it. Whole points, because the engine rounds
   * before it pays rather than after. Skill rather than the shown rating,
   * because that is what the bounty is scaled by; a season of attendance does
   * not make you the table favourite.
   */
  const bounty = bountiesFor(
    view.seats
      .filter((s) => s.pilotId && s.deckId)
      .map((s) => ({
        pilotId: s.pilotId!,
        pilotSkill: skillOf(pilot(s.pilotId!) ?? { elo: 0 }),
        deckElo: deck(s.deckId!)?.elo ?? 0,
        deckGames: deck(s.deckId!)?.placementGames ?? 0,
      })),
  ).get(mine.pilotId ?? '');
  /*
   * Round the table from your left, which is the order the turn will reach
   * them in. Drawn left to right across the far side, exactly where they are
   * sitting relative to you.
   */
  const arranged = seatsInOrder(view);
  const at = arranged.findIndex((s) => s.index === mySeat);
  const others = [...arranged.slice(at + 1), ...arranged.slice(0, at)];
  const active = pilot(view.seats[view.activeSeat]?.pilotId ?? '');
  const host = isHost;
  const n = view.seats.length;
  const back = session.game ? stepBack(session.game) : null;

  const pad = (seat: (typeof arranged)[number], isMine: boolean) => (
    <SeatPad
      key={seat.pilotId ?? seat.index}
      seat={seat}
      session={session}
      view={view}
      // Nobody else is reading this screen, so nothing on it is turned away.
      flipped={false}
      mine={isMine}
      /*
       * You can end your own turn, and the host can end anybody's. Everyone
       * else gets the deck name in that corner instead of a button the server
       * would refuse.
       */
      canPassTurn={isMine || host}
      /*
       * Your own pad, or anybody's if you are the host.
       *
       * Not offered on somebody else's pad from your own phone: the server
       * takes it from whoever holds the turn too, but a button on their card
       * that only sometimes works is worse than no button, and the person who
       * ended their turn early is looking at their own screen.
       */
      canTakeBack={back?.seat === seat.index && (isMine || host)}
      onTakeBack={turn.back}
      onCommander={onCommander}
      onPoison={onPoison}
      showPoison={showPoison}
      onOut={out.set}
      outBusy={out.busy}
      turnBusy={turn.busy}
      onPassTurn={turn.pass}
    />
  );

  return (
    /*
     * The same table, seen from your chair.
     *
     * Identical pads in the same kind of grid, with the two differences that
     * follow from this being one person's phone rather than the table's:
     * nothing is rotated, because every word on it is for you, and your row is
     * given half again the height, because yours is the pad you touch a hundred
     * times a game and theirs are read far more often than they are changed.
     */
    <div className={`table-view player seats-${n}`}>
      {others.map((seat) => pad(seat, false))}

      {/* The same hub the table has, sitting on the seam between the far side
          and your own pad: whose turn it is, which turn, and the way into
          everything else. One target, reachable with the thumb already on the
          screen. */}
      <button className="table-centre" onClick={onMenu} aria-label="Game menu">
        <Avatar name={active?.name ?? '?'} src={active?.avatar} size={36} />
        <span className="table-turn-label">Round</span>
        <span className="table-turn num">{view.turn}</span>
      </button>

      {pad(mine, true)}

      {/*
        What a takedown is worth to you, here, tonight.

        On your own screen because the number is *yours* — the bounty scales by
        your seat, so the underdog at the table is looking at a bigger one than
        the favourite is, and a rule whose value nobody can see is not a rule
        anybody plays towards. The whole reason it varies is to be read.
      */}
      {!isOut(mine) && bounty && Object.keys(bounty).length > 0 && (
        <div className="seat-bounty">
          <span className="seat-bounty-label">Takedown</span>
          {others
            .filter((s) => s.pilotId && bounty[s.pilotId] !== undefined)
            .map((s) => {
              const amount = bounty[s.pilotId!]!;
              // Anybody above you pays more than level, and gets marked as the
              // seat worth going after.
              const prize = amount > TAKEDOWN_LEVEL;
              return (
                <span
                  key={s.index}
                  className={`seat-bounty-target${prize ? ' prize' : ''}`}
                >
                  <span className="num">+{amount}</span>{' '}
                  {pilot(s.pilotId ?? '')?.name}
                </span>
              );
            })}
        </div>
      )}


      {/* Said quietly, and only when it is true. A phone holding changes it has
          not managed to send is still a working counter. */}
      {!session.online && (
        <div className="player-offline">
          Offline{session.queued > 0 ? ` · ${session.queued} to send` : ''}
        </div>
      )}

      <TakedownSheet view={view} out={out} />
    </div>
  );
}

// ---- The menu ---------------------------------------------------------------

/**
 * Everything that is not counting life.
 *
 * Behind one tap because none of it happens twice a game, and a table full of
 * controls you never use is a table you have to look at to find the ones you do.
 */
function GameMenu({
  game,
  view,
  session,
  isHost,
  mode,
  mySeat,
  canSwitch,
  onFeed,
  onMode,
  showPoison,
  onShowPoison,
  onEnd,
  onClose,
}: {
  game: LiveGame;
  view: LiveView;
  session: LiveSession;
  isHost: boolean;
  mode: CounterMode;
  /** Where the person reading this is sitting, or -1 if they are watching. */
  mySeat: number;
  /** Only somebody with a seat has a personal view to switch to. */
  canSwitch: boolean;
  onFeed: () => void;
  onMode: (mode: CounterMode) => void;
  showPoison: boolean;
  onShowPoison: (on: boolean) => void;
  onEnd: () => void;
  onClose: () => void;
}) {
  const { pilot } = useLookups();
  const [immersive, setImmersive] = useState(() =>
    document.body.classList.contains('immersive'),
  );

  const [moving, setMoving] = useState(false);
  const out = useOut(session);
  // The turns taken so far, which is what the life chart is plotted against.
  const live = columnsOf(turnStepsOf(game), view.turn);

  function toggleImmersive() {
    const next = !immersive;
    document.body.classList.toggle('immersive', next);
    setImmersive(next);
  }

  function move(from: number, to: number) {
    if (moving) return;
    setMoving(true);
    void reorderTable(game.id, from, to)
      .then(session.put)
      .catch(() => void session.refresh())
      .finally(() => setMoving(false));
  }

  const host = pilot(game.hostPilotId);

  // Labelled rather than titled: "Game" named the dialog for a screen reader,
  // and told everyone else nothing they could not already see.
  return (
    <Sheet label="Game" onClose={onClose}>
      <div className="section-head" style={{ marginTop: 0 }}>
        <h2>Finish</h2>
      </div>
      {isHost ? (
        // Gold, not red. Ending the game is what this sheet is for and what
        // everyone wants by then — a warning colour on the expected action
        // teaches people to ignore the warning colour.
        <button
          className="btn primary block"
          onClick={() => {
            onClose();
            onEnd();
          }}
        >
          End game
        </button>
      ) : (
        /*
         * One phone ends the game, and it is the one that started it.
         *
         * Not a permission so much as a guard against the same evening being
         * ended twice from two sides of the table. Everyone else is told whose
         * job it is rather than shown a button that will be refused.
         */
        <p className="hint" style={{ margin: '0 2px' }}>
          {host?.name ?? 'The host'} finishes the game and confirms who won.
        </p>
      )}

      {/*
        Your own way out, and only ever your own.
        
        Conceding is a thing you say, not a thing done to you, so nobody else's
        is reachable from here - theirs is in their own sheet. It lives in the
        menu rather than under the pads because it sat one fat thumb away from
        the life buttons, and it is the one control on this screen that cannot
        be taken back by pressing it again.
      */}
      {view.seats[mySeat] && !isOut(view.seats[mySeat]) && (
        <button
          className="btn ghost block"
          disabled={out.busy}
          onClick={() => {
            out.set(mySeat, true);
            onClose();
          }}
        >
          I&apos;m out
        </button>
      )}

      {/*
        How the game has gone so far.

        The same chart the result screen ends on, except this one is being read
        mid-game — "who is actually ahead" is a question a table asks out loud
        about twice an evening, and until now the only honest answer was four
        numbers on four sides of a phone that nobody can see at once.
      */}
      {game.log.length > 0 && (
        <>
          <div className="section-head">
            <h2>Life</h2>
            <span className="hint num">T{view.turn}</span>
          </div>
          {/* One column per turn taken, so the chart shows what each player was
              on when it came round to them rather than one figure for the whole
              round. */}
          <LifeTimeline
            ids={view.seats.map((s) => s.pilotId ?? '')}
            series={lifeSeries(
              view.seats.map((s) => s.pilotId ?? ''),
              // Corrections folded in, so an overshoot reads as one stroke
              // rather than a hit and a heal.
              foldAmendments(game.log).map((c) => ({
                // The live log is keyed by seat, because seats are what the
                // table has; the chart speaks pilotIds like everything else.
                to: view.seats[c.seat]?.pilotId ?? '',
                delta: c.delta,
                turn: c.turn,
                step: c.step,
              })),
              live,
              // The totals on the table win over the log, which can be a
              // second behind while a burst of taps is still settling.
              Object.fromEntries(view.seats.map((s) => [s.pilotId ?? '', session.lifeOf(s.index)])),
            )}
            columns={live}
          />
        </>
      )}

      <div className="section-head">
        <h2>Twitter feed</h2>
      </div>
      <button className="btn ghost block" onClick={onFeed}>
        Read the feed
      </button>

      <div className="section-head">
        <h2>Screen</h2>
      </div>
      {/*
        Which way round this phone is being used.

        Not decided once and for all: the phone in the middle of the table is
        sometimes also somebody's phone, and the person who put it down there is
        allowed to pick it back up. Everybody opens on their own seat, including
        the host, who is a player too - putting the phone in the middle is the
        deliberate act, so it is the one that takes a tap.
      */}
      {canSwitch && (
        <div className="btn-row" style={{ marginBottom: 10 }}>
          <button
            className={`btn ${mode === 'player' ? 'primary' : 'ghost'}`}
            onClick={() => onMode('player')}
          >
            My seat
          </button>
          <button
            className={`btn ${mode === 'table' ? 'primary' : 'ghost'}`}
            onClick={() => onMode('table')}
          >
            Whole table
          </button>
        </div>
      )}
      <button className="btn ghost block" onClick={toggleImmersive}>
        {immersive ? 'Minimize' : 'Maximize'}
      </button>

      {/* Off by default: most games contain no poison at all, and a skull
          reading zero on every pad all evening is furniture on a screen where
          every millimetre is contested. Kept on this phone rather than on the
          guild — it is a preference about your counter, not a rule about the
          league, and nobody should need a leader to turn it on for the one
          night somebody brings infect. */}
      <label className="menu-toggle">
        <input
          type="checkbox"
          checked={showPoison}
          onChange={(e) => onShowPoison(e.target.checked)}
        />
        <span>
          Poison counters
          <span className="hint" style={{ display: 'block' }}>
            {showPoison
              ? 'Shown on every pad.'
              : 'Hidden until somebody actually has one.'}
          </span>
        </span>
      </label>

      {/*
        Where everyone is sitting, and the host's ability to fix it.

        Only the seating plan moves. Every change ever recorded points at a seat
        by index, and those indices never change — so rearranging cannot disturb
        a life total, reassign anybody's damage, or hand the turn to somebody
        else. Turn order does follow the new arrangement, which is the point:
        Commander turn order is physical, so a table drawn in the wrong order
        was passing turns in the wrong order too.
      */}
      <div className="section-head">
        <h2>Table</h2>
        {isHost && view.seats.length > 2 && <span className="hint">move to match the room</span>}
      </div>
      <div className="list">
        {seatsInOrder(view).map((seat, position) => (
          <div className="list-item" key={seat.pilotId ?? seat.index}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <Avatar
                name={pilot(seat.pilotId ?? '')?.name ?? '?'}
                src={pilot(seat.pilotId ?? '')?.avatar}
                size={28}
              />
              <span style={{ opacity: isOut(seat) ? 0.55 : 1 }}>
                {pilot(seat.pilotId ?? '')?.name}
              </span>
              {seat.index === view.activeSeat && !isOut(seat) && (
                <span className="pip gold">now</span>
              )}
              {isOut(seat) && <span className="pip">out</span>}
              {seat.pilotId === game.hostPilotId && <span className="pip">host</span>}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="num">{session.lifeOf(seat.index)}</span>
              {isHost && view.seats.length > 2 && (
                <>
                  <button
                    className="life-step small"
                    disabled={position === 0 || moving}
                    aria-label={`move ${pilot(seat.pilotId ?? '')?.name ?? 'this seat'} up`}
                    onClick={() => move(position, position - 1)}
                  >
                    ↑
                  </button>
                  <button
                    className="life-step small"
                    disabled={position === view.seats.length - 1 || moving}
                    aria-label={`move ${pilot(seat.pilotId ?? '')?.name ?? 'this seat'} down`}
                    onClick={() => move(position, position + 1)}
                  >
                    ↓
                  </button>
                </>
              )}
            </span>
          </div>
        ))}
      </div>

      {/* Up and down are the same move when there are only two of you, so a
          head-to-head gets one button that says what it does. */}
      {isHost && view.seats.length === 2 && (
        <button
          className="btn ghost block"
          style={{ marginTop: 10 }}
          disabled={moving}
          onClick={() => move(0, 1)}
        >
          Swap seats
        </button>
      )}

      {isHost && view.seats.length > 2 && (
        <p className="hint" style={{ margin: '8px 2px 0' }}>
          Match the order to where people actually sat. Turns follow it from here.
          Nothing already recorded moves.
        </p>
      )}

      {/*
        Out of the game, said out loud.

        Not always a life total — people concede, or scoop to a combo they
        cannot answer, and until now the counter had no way to be told. Once
        somebody is marked out the turn stops coming round to them, and the
        moment it happened is what puts them in the finishing order.
      */}
      <div className="section-head">
        <h2>Knocked out</h2>
      </div>
      <div className="chips">
        {seatsInOrder(view).map((seat) => (
          <button
            key={seat.pilotId ?? seat.index}
            className="chip"
            aria-pressed={isOut(seat)}
            disabled={out.busy}
            onClick={() => out.set(seat.index, !isOut(seat))}
          >
            {pilot(seat.pilotId ?? '')?.name ?? 'Seat'}
          </button>
        ))}
      </div>
      <p className="hint" style={{ margin: '8px 2px 0' }}>
        Tap somebody who is out of the game, whatever their life total says. Their
        turns stop coming round. Tap again if they are somehow still in it.
      </p>

      {/* Opens over this sheet rather than replacing it, so answering the
          question puts you back where you were with the menu still open. */}
      <TakedownSheet view={view} out={out} />
    </Sheet>
  );
}

/** Poison pools from every source, unlike commander damage. */
function Poison({
  session,
  view,
  seatIndex,
  onClose,
}: {
  session: LiveSession;
  view: LiveView;
  seatIndex: number;
  onClose: () => void;
}) {
  const { pilot } = useLookups();
  const seat = view.seats[seatIndex] as DerivedSeat;
  const count = seat.poison;
  const dead = count >= LETHAL_POISON;

  return (
    <Sheet title={`Poison on ${pilot(seat.pilotId ?? '')?.name ?? ''}`} onClose={onClose}>
      <p className="hint" style={{ marginTop: -4, marginBottom: 18 }}>
        {LETHAL_POISON} counters is lethal, whatever the life total. Unlike commander damage
        it pools from every source.
      </p>

      <div className="counter-row">
        <button
          className="life-step"
          onClick={() => session.bumpPoison(seatIndex, -1)}
          aria-label="one fewer poison counter"
        >
          −
        </button>
        <span className={`counter-value num${dead ? ' lethal' : ''}`}>
          {count}
          <span className="counter-of">/ {LETHAL_POISON}</span>
        </span>
        <button
          className="life-step"
          onClick={() => session.bumpPoison(seatIndex, 1)}
          aria-label="one more poison counter"
        >
          +
        </button>
      </div>

      <button className="btn primary block" style={{ marginTop: 20 }} onClick={onClose}>
        Done
      </button>
    </Sheet>
  );
}

/**
 * Commander damage is per source, not a single total: twenty-one from one
 * commander kills you and twenty from each of three does not.
 */
function CommanderDamage({
  session,
  view,
  seatIndex,
  onClose,
}: {
  session: LiveSession;
  view: LiveView;
  seatIndex: number;
  onClose: () => void;
}) {
  const { pilot, deck } = useLookups();
  const seat = view.seats[seatIndex] as DerivedSeat;
  const target = pilot(seat.pilotId ?? '');

  return (
    <Sheet title={`Commander damage on ${target?.name ?? ''}`} onClose={onClose}>
      <p className="hint" style={{ marginTop: -4, marginBottom: 14 }}>
        {LETHAL_COMMANDER_DAMAGE} from any single commander is lethal, however much life is
        left.
      </p>

      <div className="list">
        {view.seats
          .filter((_, i) => i !== seatIndex)
          .flatMap((other) => {
            const from = pilot(other.pilotId ?? '');
            const theirs = deck(other.deckId ?? '');
            const pair = Boolean(theirs?.partner);
            /*
             * A row per commander, not per player.
             *
             * A partner pair is two commanders sharing a seat and each has its
             * own twenty-one, so pooling them would kill somebody who was never
             * dead. A deck with one commander keeps the row it always had,
             * labelled with the player's name - naming the commander there
             * would be a worse answer to "who is hitting me".
             */
            const commanders = pair
              ? [
                  { key: commanderKey(other.pilotId ?? ''), name: theirs?.name ?? '' },
                  {
                    key: commanderKey(other.pilotId ?? '', true),
                    name: theirs?.partner?.name ?? '',
                  },
                ]
              : [{ key: commanderKey(other.pilotId ?? ''), name: from?.name ?? '' }];

            return commanders.map(({ key, name }) => {
              const dealt = seat.commanderDamage[key] ?? 0;
              const lethal = dealt >= LETHAL_COMMANDER_DAMAGE;
              return (
                <div className="list-item" key={key}>
                  <span style={{ color: lethal ? 'var(--danger)' : undefined }}>
                    {name}
                    {lethal && ' · lethal'}
                    {pair && <span className="sub">{from?.name}</span>}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      className="life-step small"
                      onClick={() => session.bumpCommander(seatIndex, key, -1)}
                    >
                      −
                    </button>
                    <span
                      className="num"
                      style={{ minWidth: 26, textAlign: 'center', fontSize: 17 }}
                    >
                      {dealt}
                    </span>
                    <button
                      className="life-step small"
                      onClick={() => session.bumpCommander(seatIndex, key, 1)}
                    >
                      +
                    </button>
                  </span>
                </div>
              );
            });
          })}
      </div>

      <button className="btn primary block" style={{ marginTop: 16 }} onClick={onClose}>
        Done
      </button>
    </Sheet>
  );
}
